import { useState } from 'react';

export default function JobRequest() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipoEvento: '',
    fecha: '',
    ubicacion: '',
    numeroPersonas: '',
    presupuesto: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5komc2deasqilubfutg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          tipoEvento: '',
          fecha: '',
          ubicacion: '',
          numeroPersonas: '',
          presupuesto: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="job-request" className="py-24 bg-[#F5F5F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-6 py-2 bg-[#D4AF37]/10 rounded-full border border-[#D4AF37]/20">
            <span className="text-[#D4AF37] text-sm font-semibold tracking-wide">REQUEST A JOB</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-gray-900 font-bold mb-6">
            Apply for a Position
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            Join our team of professional event staff. Fill out the form below and we'll contact you soon.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl border border-gray-100">
            <form id="job-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="+1 (469) 924 9598"
                  />
                </div>

                <div>
                  <label htmlFor="tipoEvento" className="block text-sm font-semibold text-gray-900 mb-2">
                    Position of Interest *
                  </label>
                  <select
                    id="tipoEvento"
                    name="tipoEvento"
                    value={formData.tipoEvento}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Select a position</option>
                    <option value="server">Server</option>
                    <option value="bartender">Bartender</option>
                    <option value="cook">Cook / Chef</option>
                    <option value="dishwasher">Dishwasher</option>
                    <option value="line-cook">Line Cook</option>
                    <option value="sous-chef">Sous Chef</option>
                    <option value="houseman">Houseman</option>
                    <option value="barista">Barista</option>
                    <option value="executive-chef">Executive Chef</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fecha" className="block text-sm font-semibold text-gray-900 mb-2">
                    Available Start Date *
                  </label>
                  <input
                    type="date"
                    id="fecha"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                  />
                </div>

                <div>
                  <label htmlFor="ubicacion" className="block text-sm font-semibold text-gray-900 mb-2">
                    Current Location *
                  </label>
                  <input
                    type="text"
                    id="ubicacion"
                    name="ubicacion"
                    value={formData.ubicacion}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="City, State"
                  />
                </div>

                <div>
                  <label htmlFor="numeroPersonas" className="block text-sm font-semibold text-gray-900 mb-2">
                    Years of Experience *
                  </label>
                  <input
                    type="number"
                    id="numeroPersonas"
                    name="numeroPersonas"
                    value={formData.numeroPersonas}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="E.g: 3"
                  />
                </div>

                <div>
                  <label htmlFor="presupuesto" className="block text-sm font-semibold text-gray-900 mb-2">
                    Expected Hourly Rate
                  </label>
                  <select
                    id="presupuesto"
                    name="presupuesto"
                    value={formData.presupuesto}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Select a range</option>
                    <option value="16-18">$16 - $18/hour</option>
                    <option value="18-20">$18 - $20/hour</option>
                    <option value="20-22">$20 - $22/hour</option>
                    <option value="22-25">$22 - $25/hour</option>
                    <option value="25-plus">$25+/hour</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-900 mb-2">
                  Tell us about your experience *
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
                  placeholder="Describe your relevant experience, skills, and why you'd be a great fit for our team..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.mensaje.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                  <div>
                    <p className="text-green-800 font-semibold">Application sent successfully!</p>
                    <p className="text-green-700 text-sm mt-1">
                      We will review your application and contact you soon.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                  <div>
                    <p className="text-red-800 font-semibold">Error sending application</p>
                    <p className="text-red-700 text-sm mt-1">
                      Please try again or contact us directly.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] text-white py-4 rounded-full font-semibold hover:bg-[#C5A028] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <i className="ri-send-plane-fill"></i>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              Questions about the position? Call us at{' '}
              <a href="tel:+14699249598" className="text-[#D4AF37] font-semibold hover:text-[#C5A028]">
                +1 (469) 924 9598
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
